import { ChevronRightIcon } from '@chakra-ui/icons'
import { Box, Link as ChakraLink, Text } from '@chakra-ui/react'
import { useStores } from 'hooks/useStores'
import get from 'lodash/get'
import { observer } from 'mobx-react'
import { Link as RouterLink } from 'react-router-dom'
import colors from 'theme/colors.theme'
import Badge from 'components/Badge'
import { SidebarItem } from 'components/LeftSidebar/components/SideBarContent/constants'
import SvgIcon from 'components/SvgIcon'
import routes from 'routes'
import useBreakPoint from 'hooks/useBreakPoint'
import { EBreakPoint } from 'constants/theme'

interface IMenuItemLinkProps {
  isExpanded?: boolean
  isActive?: boolean
  isCondensed?: boolean
  item: SidebarItem
}

// Cyan accent stroke + horizontal fade selected-row background per RallyPD sidebar redesign.
const ACTIVE_BG_GRADIENT =
  'linear(to-r, rgba(0, 176, 255, 0.15) 0%, rgba(0, 68, 98, 0.06) 80.59%, rgba(0, 0, 0, 0) 100%)'

const MenuItemLink = ({ isExpanded, isActive, isCondensed, item }: IMenuItemLinkProps) => {
  const { notificationStore, messageStore, authStore, generalStore } = useStores()
  const { currentMembershipId } = authStore
  const { countNotifications = 0 } = notificationStore
  const { unreadSupportThreadCount = 0 } = messageStore
  const { showSidebarMobile } = generalStore
  const isMobile: boolean = useBreakPoint(EBreakPoint.BASE, EBreakPoint.MD)

  function routeTo(): string {
    if (item?.path === routes?.processes?.value) {
      // Guard: if currentMembershipId is not yet resolved, navigate without the
      // filter rather than sending an undefined/null value that produces no results.
      if (currentMembershipId != null) {
        return `${routes?.processes?.value}?creatorIds=${currentMembershipId}`
      }
      return routes?.processes?.value ?? ''
    }
    return String(get(item, 'children[0].path') ? get(item, 'children[0].path') : item?.path) || ''
  }

  function handleClick() {
    isMobile && generalStore.setShowSidebarMobile(!showSidebarMobile)
    document?.body?.classList?.remove('sidebar-enable')
  }

  const isNotifBadgeVisible = routes.notifications.value === item.path && countNotifications > 0
  const isMsgBadgeVisible = routes.messages.value === item.path && unreadSupportThreadCount > 0
  const hasChildren = !!item?.children?.length
  const counterBoxProps = {
    ml: 'auto',
    h: '21px',
    lineHeight: '21px',
    px: '4px',
    bg: '#4ca817',
    borderRadius: '4px',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '14px',
    color: '#e2e8f0'
  } as const

  // Layout shared by the navigable row and the disabled placeholder row, so the
  // two variants can never drift apart visually.
  const rowProps = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: isCondensed ? 'center' : 'flex-start',
    gap: '8px',
    px: isCondensed ? '0' : '12px',
    py: '8px',
    borderLeft: '2px solid',
    color: colors.gray[200],
    title: isCondensed ? item.name : undefined
  } as const

  const rowContent = (
    <>
      {item?.iconName && (
        <SvgIcon size={isCondensed ? 20 : 18} iconName={item.iconName} color={colors.gray[200]} />
      )}
      {!isCondensed && item.badge && <Badge color="success">{item.badge.text}</Badge>}
      {!isCondensed && (
        <Text
          as="span"
          fontFamily="Poppins"
          fontSize="14px"
          fontWeight={500}
          lineHeight="20px"
          color={colors.gray[200]}
        >
          {item.name}
        </Text>
      )}
      {!isCondensed && isNotifBadgeVisible && <Box {...counterBoxProps}>{countNotifications}</Box>}
      {!isCondensed && isMsgBadgeVisible && <Box {...counterBoxProps}>{unreadSupportThreadCount}</Box>}
      {!isCondensed && hasChildren && (
        <ChevronRightIcon
          ml="auto"
          boxSize="24px"
          transition="transform 0.3s ease"
          transform={isExpanded ? 'rotate(90deg)' : undefined}
        />
      )}
    </>
  )

  // Placeholder entries (destination page not built yet) keep the row layout but
  // never navigate and carry no active/hover affordance.
  if (item.isDisabled) {
    return (
      <Box
        {...rowProps}
        borderLeftColor="transparent"
        role="button"
        aria-disabled
        title={isCondensed ? `${item.name} — coming soon` : 'Coming soon'}
        cursor="not-allowed"
        opacity={0.5}
      >
        {rowContent}
      </Box>
    )
  }

  return (
    <ChakraLink
      as={RouterLink}
      to={routeTo()}
      onClick={handleClick}
      {...rowProps}
      borderLeftColor={isActive ? '#0EA5E9' : 'transparent'}
      bgGradient={isActive ? ACTIVE_BG_GRADIENT : undefined}
      _hover={{
        textDecoration: 'none',
        bg: isActive ? undefined : 'whiteAlpha.50'
      }}
      _focus={{ boxShadow: 'none' }}
    >
      {rowContent}
    </ChakraLink>
  )
}

export default observer(MenuItemLink)
